import React, { useState, useEffect, useContext } from "react";
import isEqual from "lodash/isEqual";
import memoize from "lodash/memoize";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useCurrentUser } from "hooks";
import { LEASE_TYPE } from "util/constants";
import toSafeInteger from "lodash/toSafeInteger";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import { track, analyticsEvents } from "util/analytics";
import SaveSearchModal from "components/componentLibrary/Modals/SaveSearchModal";

import styles from "./styles.module.scss";

export const CreateRenterSearchActiveRequirementsMutation = gql`
  mutation CreateRenterSearchActiveRequirementsMutation(
    $city: String
    $campaignId: ID!
    $maxDaysOld: Int
    $geojson: String
    $minSquareFeet: Int
    $maxSquareFeet: Int
    $statuses: [String]!
    $leaseTerm: LeaseTerm
    $leaseTypes: [EsLeaseType]
    $neighborhoodIds: [ID]
    $minMonthlyBudget: Int
    $maxMonthlyBudget: Int
    $numberOfEmployees: Int
    $neighborhoodNames: [String]
    $minPricePerSquareFoot: Float
    $maxPricePerSquareFoot: Float
    $areaExternalIds: [ID]
    $marketExternalId: ID
    $minOccupancy: Int
    $maxOccupancy: Int
    $activeSizeFilter: ActiveRequirementsSizeMode
    $activePriceFilter: ActiveRequirementsPriceMode
  ) {
    createRenterSearchActiveRequirements(
      input: {
        city: $city
        geojson: $geojson
        statuses: $statuses
        leaseTerm: $leaseTerm
        leaseTypes: $leaseTypes
        campaignId: $campaignId
        maxDaysOld: $maxDaysOld
        minSquareFeet: $minSquareFeet
        maxSquareFeet: $maxSquareFeet
        neighborhoodIds: $neighborhoodIds
        minMonthlyBudget: $minMonthlyBudget
        maxMonthlyBudget: $maxMonthlyBudget
        neighborhoodNames: $neighborhoodNames
        numberOfEmployees: $numberOfEmployees
        minPricePerSquareFoot: $minPricePerSquareFoot
        maxPricePerSquareFoot: $maxPricePerSquareFoot
        areaExternalIds: $areaExternalIds
        marketExternalId: $marketExternalId
        minOccupancy: $minOccupancy
        maxOccupancy: $maxOccupancy
        activeSizeFilter: $activeSizeFilter
        activePriceFilter: $activePriceFilter
      }
    ) {
      id
      currentActiveRequirements {
        id
        minPsf
        maxPsf
        statuses
        updatedAt
        createdAt
        leaseTerm
        leaseTypes
        maxDaysOld
        minSquareFeet
        maxSquareFeet
        minMonthlyBudget
        maxMonthlyBudget
        minOccupancy
        maxOccupancy
        sizeModel
        activeSizeFilter
        activePriceFilter
        neighborhoods {
          id
          name
        }
        numberOfEmployees
        city {
          name
        }
        areas {
          id
          externalId
          name
        }
        market {
          id
          name
          position {
            lat
            lng
          }
        }
      }
    }
  }
`;

export const determineLeaseTerm = memoize(({ leaseTermMin, leaseTermMax }) => {
  if (leaseTermMax === 12) return "LESS_THAN_ONE_YEAR";

  if (leaseTermMin === 12 && leaseTermMax === 36) return "ONE_TO_TWO_YEARS";

  if (leaseTermMin === 36 && leaseTermMax === 60) return "THREE_TO_FIVE_YEARS";

  if (leaseTermMin === 60) return "MORE_THAN_FIVE_YEARS";

  return "FLEXIBLE";
});

export const determineLeaseTypes = (leaseTypes) =>
  leaseTypes.length > 0
    ? leaseTypes.map(
        (type) =>
          ({
            license_agreement: "LICENSE_AGREEMENT",
            direct_lease: "DIRECT",
            sublease: "SUBLEASE",
          }[type] || type)
      )
    : [...Object.keys(LEASE_TYPE), "UNKNOWN"];

export const determineChangeInActiveRequirements = memoize(
  ({ contextValues, currentActiveRequirements }) => {
    if (currentActiveRequirements == null) return true;

    const { neighborhoods, submarkets } = contextValues;

    const hasChanged = [
      "minSqft",
      "maxSqft",
      "leaseTerm",
      "maxMonthlyPrice",
      "minMonthlyPrice",
      "maxPricePerSquareFoot",
      "minPricePerSquareFoot",
      "minOccupancy",
      "maxOccupancy",
      "activeSizeFilter",
      "activePriceFilter",
    ].some(
      (val) => !isEqual(currentActiveRequirements[val], contextValues[val])
    );

    if (
      contextValues.leaseTypes &&
      !isEqual(
        determineLeaseTypes(contextValues.leaseTypes).sort(),
        determineLeaseTypes(currentActiveRequirements.leaseTypes || []).sort()
      )
    )
      return true;
    if (hasChanged) return true;

    const neighborhoodsAreEqual = isEqual(
      neighborhoods.map((n) => n.name).sort(),
      (currentActiveRequirements?.areas || [])
        .filter(({ __typename }) => __typename === "Neighborhood")
        .map((n) => n.name)
        .sort()
    );
    const submarketsAreEqual = isEqual(
      submarkets.map(({ externalId }) => ({ externalId })).sort(),
      (currentActiveRequirements?.areas || [])
        .filter(({ __typename }) => __typename === "Submarket")
        .map(({ externalId }) => ({ externalId }))
        .sort()
    );

    if (!neighborhoodsAreEqual || !submarketsAreEqual) return true;

    return false;
  }
);

const SaveActiveRequirementsButton = () => {
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSavedSearchCTA, toggleSavedSearchCTA] = useState(false);

  const context = useContext(QueryManagerContext);

  const { currentUser, loading } = useCurrentUser();

  const [createRenterSearchActiveRequirements] = useMutation(
    CreateRenterSearchActiveRequirementsMutation
  );

  useEffect(() => {
    if (saved) {
      setTimeout(() => {
        setSaved(null);
      }, 5000);
    }
  }, [saved]);

  const { values } = context;

  const onSave = (user) => {
    if (!user?.latestCampaign?.id) {
      setSaved(false);
      return Promise.reject(
        new Error("A renter campaign is required to save search.")
      );
    }
    setSaving(true);

    const {
      latestCampaign: { id },
    } = user;

    const {
      leaseTermMin,
      leaseTermMax,
      market,
      maxSqft,
      minSqft,
      numEmployees,
      maxMonthlyPrice,
      minMonthlyPrice,
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
      neighborhoods,
      submarkets,
      minOccupancy,
      maxOccupancy,
      activeSizeFilter,
      activePriceFilter,
      leaseTypes = [],
    } = values;
    const leaseTerm = determineLeaseTerm({ leaseTermMin, leaseTermMax });

    const variables = {
      marketExternalId: market.externalId,
      leaseTerm,
      statuses: [
        "available",
        "offers_in",
        "squarefoot_offers_in",
        "lease_out",
        "squarefoot_lease_out",
      ],
      leaseTypes: determineLeaseTypes(leaseTypes),
      campaignId: id,
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
      minSquareFeet: minSqft,
      maxSquareFeet: maxSqft,
      minMonthlyBudget: minMonthlyPrice,
      maxMonthlyBudget: maxMonthlyPrice,
      minOccupancy,
      maxOccupancy,
      activeSizeFilter,
      activePriceFilter,
      numberOfEmployees: toSafeInteger(numEmployees),
      areaExternalIds: [
        ...submarkets.map(({ externalId }) => externalId),
        ...neighborhoods.map(({ externalId }) => externalId),
      ],
    };

    return createRenterSearchActiveRequirements({
      variables,
    })
      .then(() => {
        setSaved(true);
      })
      .catch((error) => {
        setSaved(false);
        throw error;
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading || (currentUser && !currentUser.isRenter)) {
    return null;
  }
  if (!currentUser) {
    const onConfirm = (user) => onSave(user);
    const { city, numEmployees, neighborhoods, submarkets, market } = values;
    return (
      <div key="saved" className={styles.saveContainer}>
        <button
          data-id="onboardingPill"
          type="button"
          onClick={() => {
            track(analyticsEvents.clicked, {
              category: "Public Search CTAs",
              label: "Save Search",
            });
            toggleSavedSearchCTA(true);
          }}
          className={`${styles.saveSearchPill} btn-small`}
        >
          Save Search
        </button>
        {showSavedSearchCTA && (
          <SaveSearchModal
            city={city}
            submarkets={submarkets}
            market={market.externalId}
            neighborhoods={neighborhoods}
            numberOfEmployees={parseInt(numEmployees, 10)}
            onClose={() => {
              toggleSavedSearchCTA(false);
            }}
            onConfirm={onConfirm}
          />
        )}
      </div>
    );
  }

  const currentActiveRequirements =
    currentUser.latestCampaign?.currentActiveRequirements;

  const { leaseTermMin, leaseTermMax } = values;

  const leaseTerm = determineLeaseTerm({ leaseTermMin, leaseTermMax });

  if (saved) {
    return (
      <div key="saved" className={styles.saveContainer}>
        <div className={styles.saved}>
          Search saved
          <i className={`icon icon-check-alt ${styles.checkIcon}`} />
        </div>
      </div>
    );
  }

  if (saving) {
    return (
      <div key="saving" className={styles.saveContainer}>
        <div className={styles.saving}>
          Saving
          <span className={`spinner icon-spinner ${styles.spinner}`} />
        </div>
      </div>
    );
  }

  const unsavedValuesExist = determineChangeInActiveRequirements({
    currentActiveRequirements,
    contextValues: { ...values, leaseTerm },
  });

  if (unsavedValuesExist) {
    return (
      <div key="button" className={styles.saveContainer}>
        <button
          data-id="saveSearchPill"
          type="button"
          onClick={() => onSave(currentUser).catch(() => {})}
          className={`${styles.saveSearchPill} btn-small`}
        >
          Save Search
        </button>
        {saved === false && (
          <div className={styles.saveUnsuccessful}>
            Search Not Saved. Please Try Again
            <i className={`icon icon-alert-circle ${styles.checkIcon}`} />
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default SaveActiveRequirementsButton;
